import * as React from 'react';

import { Button, TextField, cn } from '@rematter/ui-web';
import { useAtom, useSetAtom } from 'jotai';
import { X } from 'lucide-react';

import {
  $selectedNodeId,
  $updateSelectedNode,
} from '../../_state/automationEditor.state';
import { useTranslations } from '../../i18n/useTranslations';

import {
  businessEventTriggerDefinition,
  httpWebhookTriggerDefinition,
  scheduleTriggerDefinition,
  emailActionDefinition,
  httpActionDefinition,
  conditionDefinition,
  delayDefinition,
  approvalDefinition,
  filterDefinition,
  switchDefinition,
  loopDefinition,
  type BaseNodeDefinition,
} from './nodes';
import type { AutomationNode } from './nodes/types';

const nodeDefinitions: Record<string, BaseNodeDefinition> = {
  BusinessEventTrigger:
    businessEventTriggerDefinition as unknown as BaseNodeDefinition,
  HttpWebhookTrigger:
    httpWebhookTriggerDefinition as unknown as BaseNodeDefinition,
  ScheduleTrigger: scheduleTriggerDefinition as unknown as BaseNodeDefinition,
  EmailAction: emailActionDefinition as unknown as BaseNodeDefinition,
  HttpRequestAction: httpActionDefinition as unknown as BaseNodeDefinition,
  Condition: conditionDefinition as unknown as BaseNodeDefinition,
  Delay: delayDefinition as unknown as BaseNodeDefinition,
  Approval: approvalDefinition as unknown as BaseNodeDefinition,
  Filter: filterDefinition as unknown as BaseNodeDefinition,
  Switch: switchDefinition as unknown as BaseNodeDefinition,
  Loop: loopDefinition as unknown as BaseNodeDefinition,
};

const getNodeDefinition = (
  key: string | undefined,
): BaseNodeDefinition | undefined => (key ? nodeDefinitions[key] : undefined);

type NodeConfigPanelProps = {
  node: AutomationNode;
  className?: string;
};

export const NodeConfigPanel: React.FC<NodeConfigPanelProps> = ({
  node,
  className,
}) => {
  const t = useTranslations();
  const [, setSelectedNodeId] = useAtom($selectedNodeId);
  const updateSelectedNode = useSetAtom($updateSelectedNode);

  const [label, setLabel] = React.useState<string>(node.data.label);
  const [description, setDescription] = React.useState<string>(
    node.data.description || '',
  );

  const handleLabelChange = React.useCallback(
    (value: string) => {
      setLabel(value);
      updateSelectedNode({ label: value });
    },
    [updateSelectedNode],
  );

  const handleDescriptionChange = React.useCallback(
    (value: string) => {
      setDescription(value);
      updateSelectedNode({ description: value });
    },
    [updateSelectedNode],
  );

  const handleClose = () => {
    setSelectedNodeId(null);
  };

  const handleConfigUpdate = React.useCallback(
    (updates: Record<string, unknown>) => {
      updateSelectedNode({ config: updates });
    },
    [updateSelectedNode],
  );

  const definition = getNodeDefinition(node.type);

  const config = node.data.config;

  const renderConfigPanel = (): React.ReactNode => {
    if (!definition) {
      return (
        <div className="text-sm text-neutral/60">
          {t.editor.config.unknownNodeType(node.type || 'none')}
        </div>
      );
    }

    const ConfigComponent = definition.ConfigComponent;
    return (
      <ConfigComponent
        config={config}
        onUpdate={handleConfigUpdate}
        nodeId={node.id}
      />
    );
  };

  return (
    <div
      className={cn(
        '@NodeConfigPanel',
        'flex h-full w-96 flex-col border-l border-neutral/10 bg-neutral/5',
        className,
      )}
    >
      <div className="flex items-center justify-between border-b border-neutral/10 p-4">
        <div className="flex items-center gap-2">
          {definition && (
            <div className={cn('rounded p-1.5')}>
              <definition.icon className="size-4" />
            </div>
          )}
          <h3 className="font-semibold">
            {definition?.label || t.editor.config.title}
          </h3>
        </div>
        <Button
          variant="ghost"
          size="small"
          icon={<X className="size-4" />}
          onPress={handleClose}
        />
      </div>

      <div className="flex-1 space-y-4 overflow-y-auto p-4">
        <div>
          <TextField
            label={t.editor.config.fields.label}
            value={label}
            onChange={handleLabelChange}
            placeholder={t.editor.config.fields.labelPlaceholder}
          />
        </div>
        <div>
          <TextField
            label={t.editor.config.fields.description}
            value={description}
            onChange={handleDescriptionChange}
            placeholder={t.editor.config.fields.descriptionPlaceholder}
          />
        </div>

        {definition && (
          <div className="border-t border-neutral/10 pt-2">
            <label className="mb-1 block text-sm font-medium">
              {t.editor.config.fields.type}
            </label>
            <div className="text-sm text-neutral/60">{definition.label}</div>
            {definition.description && (
              <p className="mt-1 text-xs text-neutral/50">
                {definition.description}
              </p>
            )}
          </div>
        )}

        <div className="border-t border-neutral/10 pt-4">
          {renderConfigPanel()}
        </div>
      </div>

      <div className="border-t border-neutral/10 p-4">
        <Button variant="outline" onPress={handleClose} className="w-full">
          {t.editor.config.close}
        </Button>
      </div>
    </div>
  );
};
